/* eslint react-hooks/exhaustive-deps: "warn" */

import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Section } from 'ui/molecules/Section/Section';

import styles from './RegisConsole.module.scss';

import { type ConsoleCountry, getConsoleScenes } from './RegisConsole.data';

const TYPE_DELAY = 16;
const STEP_DELAY = 430;
const PRE_TYPE_DELAY = 320;
const PRE_STEPS_DELAY = 340;
const PRE_RESPONSE_DELAY = 240;
const PRE_LEDGER_DELAY = 520;
const DWELL_DELAY = 5200;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const stamp = () => {
  const d = new Date();
  const p = (n: number) => (n < 10 ? `0${n}` : `${n}`);

  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}T${p(d.getUTCHours())}:${p(
    d.getUTCMinutes(),
  )}:${p(d.getUTCSeconds())}Z`;
};

const FlagSprite = () => (
  <svg aria-hidden="true" className={styles.sprite} width="0" height="0">
    <defs>
      <symbol id="rc-gb" viewBox="0 0 16 11">
        <rect fill="#012169" width="16" height="11" />
        <path d="M0 0l16 11M16 0L0 11" stroke="#fff" strokeWidth="2.2" />
        <path d="M0 0l16 11M16 0L0 11" stroke="#C8102E" strokeWidth="1.1" />
        <path d="M8 0v11M0 5.5h16" stroke="#fff" strokeWidth="3.4" />
        <path d="M8 0v11M0 5.5h16" stroke="#C8102E" strokeWidth="2" />
      </symbol>
      <symbol id="rc-us" viewBox="0 0 16 11">
        <rect fill="#B22234" width="16" height="11" />
        <rect fill="#fff" y="0.85" width="16" height="0.85" />
        <rect fill="#fff" y="2.53" width="16" height="0.85" />
        <rect fill="#fff" y="4.23" width="16" height="0.85" />
        <rect fill="#fff" y="5.92" width="16" height="0.85" />
        <rect fill="#fff" y="7.61" width="16" height="0.85" />
        <rect fill="#fff" y="9.3" width="16" height="0.85" />
        <rect fill="#3C3B6E" width="7" height="5.9" />
      </symbol>
      <symbol id="rc-it" viewBox="0 0 16 11">
        <rect fill="#fff" width="16" height="11" />
        <rect fill="#009246" width="5.34" height="11" />
        <rect fill="#CE2B37" width="5.34" height="11" x="10.66" />
      </symbol>
      <symbol id="rc-de" viewBox="0 0 16 11">
        <rect fill="#000" width="16" height="3.67" />
        <rect fill="#DD0000" width="16" height="3.67" y="3.67" />
        <rect fill="#FFCE00" width="16" height="3.66" y="7.34" />
      </symbol>
      <symbol id="rc-nl" viewBox="0 0 16 11">
        <rect fill="#21468B" width="16" height="11" />
        <rect fill="#AE1C28" width="16" height="3.67" />
        <rect fill="#fff" width="16" height="3.67" y="3.67" />
      </symbol>
    </defs>
  </svg>
);

const FlagIcon = ({ country }: { country: ConsoleCountry }) => (
  <svg className={styles.flag} viewBox="0 0 16 11" aria-hidden="true">
    <use href={`#rc-${country}`} />
  </svg>
);

export const RegisConsole = () => {
  const { t } = useInt();
  const rootRef = useRef<HTMLDivElement>(null);
  const started = useIntersection(rootRef, { threshold: 0.2 });

  const scenes = getConsoleScenes(t);

  const tokenRef = useRef(0);
  const userPickedRef = useRef(false);
  const reducedMotionRef = useRef(false);

  const [activeIndex, setActiveIndex] = useState(0);
  const [typedLength, setTypedLength] = useState(0);
  const [visibleSteps, setVisibleSteps] = useState(0);
  const [responseShown, setResponseShown] = useState(false);
  const [ledgerShown, setLedgerShown] = useState(false);
  const [statusText, setStatusText] = useState(t('awaiting query'));

  const activeScene = scenes[activeIndex];

  const showStatic = (idx: number) => {
    const scene = scenes[idx];

    setActiveIndex(idx);
    setTypedLength(scene.q.length);
    setVisibleSteps(scene.steps.length);
    setResponseShown(true);
    setLedgerShown(true);
    setStatusText(`${t('retrieved')} ${stamp()}`);
  };

  const play = async (idx: number) => {
    const token = ++tokenRef.current;
    const scene = scenes[idx];

    setActiveIndex(idx);
    setTypedLength(0);
    setVisibleSteps(0);
    setResponseShown(false);
    setLedgerShown(false);
    setStatusText(t('awaiting query'));

    await sleep(PRE_TYPE_DELAY);
    if (tokenRef.current !== token) return;

    for (let c = 0; c <= scene.q.length; c += 1) {
      if (tokenRef.current !== token) return;
      setTypedLength(c);

      await sleep(TYPE_DELAY);
    }

    if (tokenRef.current !== token) return;
    setStatusText(t('retrieving'));

    await sleep(PRE_STEPS_DELAY);
    if (tokenRef.current !== token) return;

    for (let s = 0; s < scene.steps.length; s += 1) {
      if (tokenRef.current !== token) return;
      setVisibleSteps(s + 1);

      await sleep(STEP_DELAY);
    }

    if (tokenRef.current !== token) return;

    await sleep(PRE_RESPONSE_DELAY);
    if (tokenRef.current !== token) return;
    setResponseShown(true);
    setStatusText(`${t('retrieved')} ${stamp()}`);

    await sleep(PRE_LEDGER_DELAY);
    if (tokenRef.current !== token) return;
    setLedgerShown(true);

    await sleep(DWELL_DELAY);
    if (tokenRef.current !== token) return;

    if (!userPickedRef.current) {
      void play((idx + 1) % scenes.length);
    }
  };

  useEffect(() => {
    if (!started) {
      return undefined;
    }

    reducedMotionRef.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reducedMotionRef.current) {
      showStatic(0);
    } else {
      void play(0);
    }

    return () => {
      tokenRef.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started]);

  const handleTabClick = (idx: number) => {
    userPickedRef.current = true;

    if (reducedMotionRef.current) {
      tokenRef.current += 1;
      showStatic(idx);
    } else {
      void play(idx);
    }
  };

  return (
    <Section className={styles.root}>
      <FlagSprite />

      <div ref={rootRef} className={cn(styles.console, started && styles.consoleIn)}>
        <div className={styles.head}>
          <span className={styles.dot} />
          <span className={styles.tag}>POST api.globaldatabase.com/v1/regis/query</span>
          <span className={styles.spacer} />
          <span className={styles.clock}>{statusText}</span>
        </div>

        <div className={styles.tabs} role="tablist" aria-label={t('Example queries')}>
          {scenes.map((scene, i) => (
            <button
              key={scene.tab}
              type="button"
              role="tab"
              aria-selected={i === activeIndex}
              className={cn(styles.tab, i === activeIndex && styles.tabActive)}
              onClick={() => handleTabClick(i)}
            >
              {scene.tab}
            </button>
          ))}
        </div>

        <div className={styles.body}>
          <span className={styles.label}>{t('Request body')}</span>

          <div className={styles.prompt}>
            <span className={styles.brace}>{'{ '}</span>
            <span className={styles.key}>&quot;prompt&quot;</span>
            <span className={styles.brace}>{': "'}</span>
            <span>{activeScene.q.slice(0, typedLength)}</span>
            {typedLength < activeScene.q.length ? (
              <span className={styles.cursor} />
            ) : (
              <span className={styles.brace}>{'" }'}</span>
            )}
          </div>

          <ul className={styles.steps}>
            {activeScene.steps.map((step, i) => (
              <li key={step.label} className={cn(styles.step, i < visibleSteps && styles.stepIn)}>
                <span className={styles.stepOk}>&#10003;</span>
                <b>{step.label}</b>
                <span dangerouslySetInnerHTML={{ __html: step.detailHtml }} />
              </li>
            ))}
          </ul>

          <div className={cn(styles.resHead, responseShown && styles.resHeadIn)}>
            <span className={styles.label}>{t('Response')}</span>
            <span className={styles.status}>200 OK</span>
            <span className={styles.ct}>application/json</span>
          </div>

          <div
            className={cn(styles.answer, responseShown && styles.answerIn)}
            dangerouslySetInnerHTML={{ __html: activeScene.answerHtml }}
          />

          <div className={cn(styles.ledger, ledgerShown && styles.ledgerIn)}>
            <div className={styles.ledgerLabel}>{t('Sources returned with the answer')}</div>
            {activeScene.ledger.map((row, i) => (
              <div
                key={`${row.field}-${row.value}`}
                className={cn(styles.ledgerRow, ledgerShown && styles.ledgerRowIn)}
                style={{ transitionDelay: ledgerShown ? `${i * 70}ms` : '0ms' }}
              >
                <span className={styles.ledgerIndex}>{i + 1}</span>
                <span className={styles.ledgerField}>
                  <em>{row.field}</em>
                  {row.value}
                </span>
                <span className={styles.ledgerSource}>
                  <FlagIcon country={row.country} />
                  {row.source}
                </span>
                <span className={styles.ledgerFiling}>{row.filing}</span>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.foot}>
          <span>{ledgerShown ? activeScene.foot : ' '}</span>
          <span className={styles.footSpacer} />
          <a className={styles.footLink} href="https://api.globaldatabase.com/docs/v2">
            {t('Full reference')}
          </a>
        </div>
      </div>
    </Section>
  );
};
